import { useTheme, useMediaQuery } from "@mui/material";
import { Beacon } from "../types/models/Beacon";
import { MakeBeacon } from "../types/models/MakeBeacon";
import { UpdateBeacon } from "../types/models/UpdateBeacon";
import { Category } from "../types/models/Category";
import dayjs from 'dayjs';


export const useWidth = () => {
    const theme = useTheme();
    const keys = [...theme.breakpoints.keys].reverse();
    return (
        keys.reduce((output, key: any) => {
            // eslint-disable-next-line react-hooks/rules-of-hooks
            const matches = useMediaQuery(theme.breakpoints.up(key));
            return !output && matches ? key : output;
        }, null) || 'xs'
    );
};

export const getDisplayDate = (date: Date) => {
    return date.toLocaleDateString('en-us', { year: "numeric", month: "short", day: "numeric" });
};

export const getDisplayTime = (date: Date) => {
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    var strTime = hours + ':' + minutesStr + ' ' + ampm;

    return strTime;
};

export const getDateTimeString = (dateString: string) => {
    if (dateString && dateString.length > 0) {
        let date = getDisplayDate(new Date(dateString));
        let time = getDisplayTime(new Date(dateString));
        return date + " " + time;
    }
    else {
        return "Always Active";
    }
};

export const getTimeDateString = (dateString: string | Date) => {
    // Front end format is different to database format.
    if (dateString instanceof Date) {
        dateString = dateString.toISOString();
    } else if (dayjs.isDayjs(dateString)) {
        dateString = dateString.toDate();
    }

    if (dateString) {
        let date = getDisplayDate(new Date(dateString));
        let time = getDisplayTime(new Date(dateString));
        return time + " " + date;
    }
    else {
        return "Always Active";
    }
};

export const getCategoryColor = (key: string, customCategories: Category[]) => {

    const selectedCategory = customCategories.find(category => category.Name === key);
    if (selectedCategory) {
        return "#" + selectedCategory.Color;
    }
    
    return "#999999"; // Default color if not found
};

export const getStatusColor = (active: Boolean) => {
    if (active) {
        return '#68A200';
    }
    else {
        return '#878787';
    }
};

export const getScheduleString = (beacon: Beacon) => {
    const startDate = new Date(beacon.start_time);
    const endDate = new Date(beacon.end_time);
    const startDisplayDate = getDisplayDate(startDate);
    const startDisplayTime = getDisplayTime(startDate);
    const endDisplayDate = getDisplayDate(endDate);
    const endDisplayTime = getDisplayTime(endDate);
    let displayDateTime = "";
    if (startDisplayDate === endDisplayDate) {
        displayDateTime = startDisplayDate + "  " + startDisplayTime + " - " + endDisplayTime;
    }
    else {
        displayDateTime = startDisplayDate + "  " + startDisplayTime + " - " + endDisplayDate + " " + endDisplayTime;
    }
    return displayDateTime;
};

export const convertByteString = (bytes: number) => {
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];

    if (bytes === 0) {
        return "0 Byte";
    }

    const i = Math.floor(Math.log(bytes) / Math.log(1024));

    if (i === 0) {
        return bytes + " " + sizes[i];
    }

    return (bytes / Math.pow(1024, i)).toFixed(1) + " " + sizes[i];
};

export const addDays = (date: Date, days: number) => {
    let result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
};

export const getImageURLOnServer = (imageId: string) => {
    const baseURL = process.env.REACT_APP_IMAGE_BASE_URL ?? "/beacon";
    return baseURL + "/" + imageId + ".jpg";
};

export const getImageURLOnServerBeaconFace = (id: string, lastModified: string) => {
    const baseURL = process.env.REACT_APP_IMAGEFACE_BASE_URL ?? "/beaconface";
    return `${baseURL}/${id}.png?${lastModified}`;
};

export const getImageURLCategoryListIcon = (id: string, lastModified: string) => {
    const baseURL = process.env.REACT_APP_IMAGECATEGORY_BASE_URL ?? "/beaconicon";
    return `${baseURL}/c_${id}.png?${lastModified}`;
};

export const getModelURLOnServer = (modelFileName: string) => {
    const baseURL = process.env.REACT_APP_API_BASE_URL ?? "";
    return `${baseURL}/beaconmodel/${modelFileName}`;
};

export const convertToUpdateBeacon = (makeBeacon: MakeBeacon, beaconId: string, beaconGroupId: string) => {
    const updateBeacon: UpdateBeacon = {
        beaconid: beaconId,
        name: makeBeacon.name,
        beacon_group_id: beaconGroupId,
        description: makeBeacon.description,
        category: makeBeacon.category,
        link: makeBeacon.link,
        summary: makeBeacon.summary,
        latitude: makeBeacon.latitude ?? 0,
        longitude: makeBeacon.longitude ?? 0,
        altitude: makeBeacon.altitude,
        startDate: makeBeacon.startDate == "" ? null : makeBeacon.startDate,
        endDate: makeBeacon.startDate == "" ? null : makeBeacon.endDate,
        image_id: makeBeacon.image_id,
        model: makeBeacon.model
    };
    return updateBeacon;
};

export const convertToUpdateBeaconCoords = (beacon: Beacon, beaconGroupId: string, latitude: number, longitude: number) => {
    const updateBeacon: UpdateBeacon = {
        beaconid: beacon.id,
        name: beacon.display_name,
        beacon_group_id: beaconGroupId,
        description: beacon.description ?? "",
        category: beacon.category ?? undefined,
        link: beacon.link ?? undefined,
        summary: beacon.summary ?? undefined,
        latitude: latitude,
        longitude: longitude,
        altitude: beacon.altitude ?? undefined,
        startDate: beacon.start_time,
        endDate: beacon.end_time,
        image_id: beacon.image_id ?? undefined,
        model: beacon.model

    };
    return updateBeacon;
};

export const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
};

export function getLogoPath(): string {
    const host = window.location.host;
    // Localhost
    if (host.includes("localhost")) return "/skidattl-testing-logo.svg";
    // Testing Environment
    if (host.includes("testing.skidattl.com")) return "/skidattl-testing-logo.svg";
  
    // Other Specific Environments can be added here.
  
    // Default Index
    const lightBgPaths = ["/login", "/register", "/activate", "/resetPassword"];
    return lightBgPaths.includes(window.location.pathname)
        ? "/skidattl-logo-lightbkg.svg"
        : "/skidattl-logo-darkbkg.svg"; // Default any other page within blender
}
